import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Alert, Box, Skeleton, Tab, Tabs, TextField } from '@mui/material';
import { EventNote as OpsIcon, AccountBalanceWallet as FinIcon, Insights as PerfIcon } from '@mui/icons-material';
import OperationsTab from './OperationsTab';
import FinanceTab from './FinanceTab';
import PerformanceTab from './PerformanceTab';
import { loadOperations } from '../../services/dashboard/operations.service';
import { loadFinance } from '../../services/dashboard/finance.service';
import { loadPerformance } from '../../services/dashboard/performance.service';
import { TABS, visibleTabs, defaultTab } from '../../utils/dashboard/access';
import { PERIODS, periodRange } from '../../utils/dashboard/period';
import { RevealGroup } from '../../components/dashboard/v2/motion';

/**
 * TC dashboard v2 (TC-DASHBOARD-V2-DESIGN): Operations · Finance · Performance & Team, one at a time.
 * The period chips sit on the tab row and are hidden on Operations (always "today"). Each tab loads when first
 * shown and again when the period changes; the chosen tab is remembered in this browser.
 */

const TAB_ICONS = { ops: OpsIcon, fin: FinIcon, perf: PerfIcon };
const STORE_KEY = 'tcDashboard.tab';
const readStored = () => { try { return localStorage.getItem(STORE_KEY); } catch (e) { return null; } };
const writeStored = (v) => { try { localStorage.setItem(STORE_KEY, v); } catch (e) { /* private mode */ } };

const PeriodChips = ({ value, onChange, custom, onCustom }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap' }}>
    {PERIODS.map((p) => (
      <Box key={p.key} component="button" type="button" onClick={() => onChange(p.key)} aria-pressed={value === p.key}
        sx={{ font: 'inherit', fontSize: '0.75rem', fontWeight: 600, px: 1.5, py: 0.75, borderRadius: 999, cursor: 'pointer', border: '1px solid',
          borderColor: value === p.key ? 'text.primary' : 'divider', bgcolor: value === p.key ? 'text.primary' : 'background.paper',
          color: value === p.key ? 'background.paper' : 'text.secondary' }}>
        {p.label}
      </Box>
    ))}
    {value === 'custom' && (
      <Box sx={{ display: 'flex', gap: 1 }}>
        <TextField size="small" type="date" label="From" InputLabelProps={{ shrink: true }} value={custom.from}
          onChange={(e) => onCustom({ ...custom, from: e.target.value })} sx={{ width: 150 }} />
        <TextField size="small" type="date" label="To" InputLabelProps={{ shrink: true }} value={custom.to}
          onChange={(e) => onCustom({ ...custom, to: e.target.value })} sx={{ width: 150 }} />
      </Box>
    )}
  </Box>
);

// Placeholders in the tab's own layout (four headline cards, then its two-column row), so nothing jumps when data lands.
const LoadingTab = ({ tab }) => (
  <Box sx={{ display: 'grid', gap: 2 }}>
    <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: '1fr', md: 'repeat(4, 1fr)' } }}>
      {[0, 1, 2, 3].map((i) => <Skeleton key={i} variant="rounded" animation="wave" height={150} sx={{ borderRadius: '16px' }} />)}
    </Box>
    <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: '1fr', md: tab === 'ops' ? '7fr 5fr' : '8fr 4fr' } }}>
      <Skeleton variant="rounded" animation="wave" height={280} sx={{ borderRadius: '16px' }} />
      <Skeleton variant="rounded" animation="wave" height={280} sx={{ borderRadius: '16px' }} />
    </Box>
  </Box>
);

const TcDashboard = ({ currentUser }) => {
  const navigate = useNavigate();
  const tabs = useMemo(() => visibleTabs(currentUser), [currentUser]);
  const [tab, setTab] = useState(() => defaultTab(currentUser, readStored()));
  const [period, setPeriod] = useState('month');
  const [custom, setCustom] = useState({ from: '', to: '' });
  const [models, setModels] = useState({});
  const [failed, setFailed] = useState(null);
  const loadedKey = useRef({});

  const range = periodRange(period, new Date(), custom);
  const rangeKey = range ? `${range.from}_${range.to}` : null;
  const periodLabel = PERIODS.find((p) => p.key === period)?.label || '';

  useEffect(() => {
    const key = tab === 'ops' ? 'today' : rangeKey;
    const loaded = loadedKey.current; // plain data (tab → key loaded), not a DOM node
    if (!key || loaded[tab] === key) return undefined;
    let active = true;
    loaded[tab] = key;
    setFailed(null);
    setModels((prev) => ({ ...prev, [tab]: null }));
    const load = tab === 'ops' ? loadOperations(new Date(), {}, currentUser)
      : tab === 'fin' ? loadFinance(range) : loadPerformance(range, new Date(), {}, currentUser);
    let done = false;
    load.then((m) => { done = true; if (active) setModels((prev) => ({ ...prev, [tab]: m })); })
      .catch(() => { done = true; if (active) { loaded[tab] = null; setFailed('Could not load the dashboard. Try again in a moment.'); } });
    // a cancelled load (period changed, or StrictMode re-running the effect) must be started again
    return () => { active = false; if (!done && loaded[tab] === key) loaded[tab] = null; };
    // range is derived from rangeKey
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, rangeKey, currentUser]);

  const pickTab = (next) => { setTab(next); writeStored(next); };
  const model = models[tab];

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1.5, flexWrap: 'wrap', mb: 2.5 }}>
        {tabs.length > 1 ? (
          <Tabs value={tab} onChange={(e, v) => pickTab(v)} variant="scrollable" scrollButtons={false}
            TabIndicatorProps={{ style: { display: 'none' } }}
            sx={{ minHeight: 0, bgcolor: 'action.hover', borderRadius: '12px', p: 0.5,
              '& .MuiTab-root': { minHeight: 0, py: 1, px: 2, borderRadius: '9px', textTransform: 'none', fontSize: '0.8125rem', fontWeight: 600, color: 'text.secondary' },
              '& .MuiTab-root.Mui-selected': { bgcolor: 'background.paper', color: 'text.primary', boxShadow: '0 1px 3px rgba(0,0,0,.08)' } }}>
            {tabs.map((k) => {
              const Icon = TAB_ICONS[k];
              return <Tab key={k} value={k} label={TABS[k]} icon={<Icon sx={{ fontSize: 18 }} />} iconPosition="start" />;
            })}
          </Tabs>
        ) : <Box />}
        {tab !== 'ops' && <PeriodChips value={period} onChange={setPeriod} custom={custom} onCustom={setCustom} />}
      </Box>

      {failed && <Alert severity="error" sx={{ mb: 2 }}>{failed}</Alert>}
      {tab !== 'ops' && !range ? (
        <Alert severity="info" sx={{ mb: 2 }}>
          {custom.from && custom.to ? 'The end date is before the start date' : 'Pick a start and end date'}
        </Alert>
      ) : !model ? (!failed && <LoadingTab tab={tab} />) : (
        <RevealGroup key={`${tab}-${tab === 'ops' ? 'today' : rangeKey}`}>
          {tab === 'ops' && <OperationsTab model={model} navigate={navigate} user={currentUser} />}
          {tab === 'fin' && <FinanceTab model={model} navigate={navigate} periodLabel={periodLabel} user={currentUser} />}
          {tab === 'perf' && <PerformanceTab model={model} navigate={navigate} />}
        </RevealGroup>
      )}
    </Box>
  );
};

export default TcDashboard;
